import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Clock,
  AlertTriangle,
  Save,
  Play,
  RefreshCw,
  Bell,
  Briefcase,
  Shield,
  TrendingUp,
  Users,
  ArrowUpRight,
  Settings
} from 'lucide-react';

const SEVERITY_CONFIG = {
  CRITICA: { label: 'Crítica', color: '#ef4444', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
  ALTA: { label: 'Alta', color: '#f97316', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
  MEDIA: { label: 'Media', color: '#eab308', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' },
  BAJA: { label: 'Baja', color: '#22c55e', bgColor: 'bg-green-50', borderColor: 'border-green-200' }
};

const EscalationConfigV2 = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('alertas');
  
  // Config para alertas (por severidad)
  const [alertConfig, setAlertConfig] = useState({
    activo: true,
    configuracion_por_severidad: {
      CRITICA: { activo: true, horas: 4, escalar_a: 'SUPERVISOR' },
      ALTA: { activo: true, horas: 12, escalar_a: 'SUPERVISOR' },
      MEDIA: { activo: true, horas: 24, escalar_a: 'ANALISTA_SENIOR' },
      BAJA: { activo: false, horas: 48, escalar_a: null }
    }
  });
  
  // Config para casos
  const [casesConfig, setCasesConfig] = useState({
    activo: true,
    configuracion_por_severidad: {
      CRITICA: { activo: true, horas: 24, escalar_a: 'SUPERVISOR' },
      ALTA: { activo: true, horas: 48, escalar_a: 'SUPERVISOR' },
      MEDIA: { activo: true, horas: 72, escalar_a: 'ANALISTA_SENIOR' },
      BAJA: { activo: false, horas: 168, escalar_a: null }
    }
  });

  // Usuarios para escalamiento
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, usersRes, groupsRes] = await Promise.all([
        api.get('/escalation/config/v2').catch(() => ({ data: null })),
        api.get('/users').catch(() => ({ data: [] })),
        api.get('/groups/').catch(() => ({ data: [] }))
      ]);
      
      if (configRes.data) {
        if (configRes.data.alertas) setAlertConfig(configRes.data.alertas);
        if (configRes.data.casos) setCasesConfig(configRes.data.casos);
      }
      
      setUsers(usersRes.data?.filter(u => u.activo && ['ADMINISTRADOR', 'SUPERVISOR'].includes(u.rol)) || []);
      setGroups(groupsRes.data || []);
    } catch (error) {
      console.error('Error cargando configuración:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/escalation/config/v2', {
        alertas: alertConfig,
        casos: casesConfig
      });
      toast.success('Configuración de escalamiento guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const updateAlertSeverityConfig = (severity, field, value) => {
    setAlertConfig(prev => ({
      ...prev,
      configuracion_por_severidad: {
        ...prev.configuracion_por_severidad,
        [severity]: {
          ...prev.configuracion_por_severidad[severity],
          [field]: value
        }
      }
    }));
  };

  const updateCaseSeverityConfig = (severity, field, value) => {
    setCasesConfig(prev => ({
      ...prev,
      configuracion_por_severidad: {
        ...prev.configuracion_por_severidad,
        [severity]: {
          ...prev.configuracion_por_severidad[severity],
          [field]: value
        }
      }
    }));
  };

  const renderSeverityCard = (severity, config, updateFn, tipo) => {
    const sevConfig = SEVERITY_CONFIG[severity];
    const itemConfig = config.configuracion_por_severidad[severity];

    return (
      <Card 
        key={severity} 
        className={`p-4 ${sevConfig.bgColor} ${sevConfig.borderColor} border-2 ${!itemConfig.activo && 'opacity-50'}`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: sevConfig.color }}
            />
            <span className="font-semibold" style={{ color: sevConfig.color }}>
              {sevConfig.label}
            </span>
          </div>
          <Switch
            checked={itemConfig.activo}
            onCheckedChange={(v) => updateFn(severity, 'activo', v)}
          />
        </div>

        <div className="space-y-3">
          {/* Horas para escalar */}
          <div>
            <Label className="text-xs text-slate-600">Escalar después de</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                min={1}
                value={itemConfig.horas}
                onChange={(e) => updateFn(severity, 'horas', parseInt(e.target.value) || 1)}
                className="w-20 h-8 text-sm"
                disabled={!itemConfig.activo}
              />
              <span className="text-sm text-slate-600">horas</span>
            </div>
          </div>

          {/* Escalar a */}
          <div>
            <Label className="text-xs text-slate-600">Escalar a</Label>
            <Select
              value={itemConfig.escalar_a || 'none'}
              onValueChange={(v) => updateFn(severity, 'escalar_a', v === 'none' ? null : v)}
              disabled={!itemConfig.activo}
            >
              <SelectTrigger className="h-8 text-sm mt-1">
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar (solo notificar)</SelectItem>
                <SelectItem value="SUPERVISOR">Supervisores</SelectItem>
                <SelectItem value="ADMINISTRADOR">Administradores</SelectItem>
                <SelectItem value="ANALISTA_SENIOR">Analistas Senior</SelectItem>
                {groups.map(g => (
                  <SelectItem key={g.id} value={`grupo_${g.id}`}>
                    Grupo: {g.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {itemConfig.activo && (
          <div className="mt-3 pt-3 border-t border-slate-200">
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <ArrowUpRight size={12} />
              {tipo === 'alertas' ? 'Alertas' : 'Casos'} {sevConfig.label.toLowerCase()}s sin atención por más de {itemConfig.horas}h 
              {itemConfig.escalar_a && ` → ${itemConfig.escalar_a}`}
            </p>
          </div>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="escalation-config-v2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="text-orange-600" />
            Escalamiento Automático
          </h1>
          <p className="text-slate-600 mt-1">
            Configura reglas de escalamiento por severidad para alertas y casos
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Save size={16} className="mr-2" />
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Settings className="text-blue-600 mt-1" size={20} />
          <div>
            <h4 className="font-semibold text-blue-900">¿Cómo funciona el escalamiento?</h4>
            <p className="text-sm text-blue-700 mt-1">
              Cuando una alerta o caso permanece sin atención (estado "NUEVA" o "ABIERTO") por más tiempo 
              del configurado, el sistema automáticamente lo escalará al rol o grupo indicado y enviará 
              una notificación. Puedes configurar diferentes tiempos y destinos según la severidad.
            </p>
          </div>
        </div>
      </Card>

      {/* Tabs para Alertas y Casos */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger 
            value="alertas" 
            className="data-[state=active]:bg-orange-100 data-[state=active]:text-orange-700"
          >
            <Bell size={16} className="mr-2" />
            Alertas
          </TabsTrigger>
          <TabsTrigger 
            value="casos"
            className="data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700"
          >
            <Briefcase size={16} className="mr-2" />
            Casos
          </TabsTrigger>
        </TabsList>

        {/* Tab: Alertas */}
        <TabsContent value="alertas">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Bell size={24} className="text-orange-600" />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Escalamiento de Alertas</h2>
                  <p className="text-sm text-slate-500">Configura el escalamiento para cada nivel de severidad</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600">Escalamiento activo</Label>
                <Switch
                  checked={alertConfig.activo}
                  onCheckedChange={(v) => setAlertConfig({ ...alertConfig, activo: v })}
                />
              </div>
            </div>

            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${!alertConfig.activo && 'opacity-50 pointer-events-none'}`}>
              {Object.keys(SEVERITY_CONFIG).map(sev => 
                renderSeverityCard(sev, alertConfig, updateAlertSeverityConfig, 'alertas')
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Tab: Casos */}
        <TabsContent value="casos">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Briefcase size={24} className="text-purple-600" />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Escalamiento de Casos</h2>
                  <p className="text-sm text-slate-500">Configura el escalamiento para casos de investigación</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-slate-600">Escalamiento activo</Label>
                <Switch
                  checked={casesConfig.activo}
                  onCheckedChange={(v) => setCasesConfig({ ...casesConfig, activo: v })}
                />
              </div>
            </div>

            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${!casesConfig.activo && 'opacity-50 pointer-events-none'}`}>
              {Object.keys(SEVERITY_CONFIG).map(sev => 
                renderSeverityCard(sev, casesConfig, updateCaseSeverityConfig, 'casos')
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Resumen de configuración */}
      <Card className="p-4 bg-slate-50">
        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
          <Shield size={18} className="text-slate-600" />
          Resumen de Configuración Activa
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-orange-700 mb-2">Alertas</h4>
            {alertConfig.activo ? (
              <ul className="space-y-1 text-sm">
                {Object.entries(alertConfig.configuracion_por_severidad)
                  .filter(([_, cfg]) => cfg.activo)
                  .map(([sev, cfg]) => (
                    <li key={sev} className="text-slate-600">
                      • {SEVERITY_CONFIG[sev].label}: {cfg.horas}h → {cfg.escalar_a || 'Notificar'}
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-400">Desactivado</p>
            )}
          </div>
          <div>
            <h4 className="text-sm font-medium text-purple-700 mb-2">Casos</h4>
            {casesConfig.activo ? (
              <ul className="space-y-1 text-sm">
                {Object.entries(casesConfig.configuracion_por_severidad)
                  .filter(([_, cfg]) => cfg.activo)
                  .map(([sev, cfg]) => (
                    <li key={sev} className="text-slate-600">
                      • {SEVERITY_CONFIG[sev].label}: {cfg.horas}h → {cfg.escalar_a || 'Notificar'}
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-400">Desactivado</p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default EscalationConfigV2;
